// components/DropboxApply.jsx
import React, { useState } from "react";
import axios from "axios";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Avatar,
  Paper,
  Snackbar,
  Alert,
  Divider,
} from "@mui/material";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

export default function DropboxApply({
  apiBase = process.env.REACT_APP_API_BASE_URL,
}) {
  const [step, setStep] = useState("email_check"); // email_check | form_entry | done
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    mobile: "",
    email: "",
  });
  const [resumeFile, setResumeFile] = useState(null);

  const showMessage = (message, severity = "info") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const reset = () => {
    setEmail("");
    setForm({ firstName: "", lastName: "", mobile: "", email: "" });
    setResumeFile(null);
    setStep("email_check");
    showMessage("Form reset.", "info");
  };

  const handleCheckEmail = async () => {
    if (!email || !email.includes("@")) {
      showMessage("Please enter a valid email.", "warning");
      return;
    }

    setLoading(true);
    showMessage("Checking email...", "info");

    try {
      const res = await axios.get(`${apiBase}/api/techsummit2025/dropbox/check`, {
        params: { email },
      });

      if (res.data.exists) {
        const u = res.data.data;
        setForm({
          firstName: u.first_name || "",
          lastName: u.last_name || "",
          mobile: u.mobile || "",
          email: u.email || email,
        });
        showMessage("Existing record fetched. Please review and update.", "success");
      } else {
        setForm((f) => ({ ...f, email }));
        showMessage("No existing record found. Please fill out the form.", "info");
      }

      setStep("form_entry");
    } catch (err) {
      console.error(err);
      showMessage(err?.response?.data?.message || "Failed to check email.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setResumeFile(file || null);
    if (file) showMessage(`File selected: ${file.name}`, "info");
  };

  const handleSubmit = async () => {
    if (!form.firstName || !form.lastName || !form.mobile || !form.email) {
      showMessage("All required fields are missing.", "error");
      return;
    }
    if (!resumeFile) {
      showMessage("Please upload your resume (PDF/DOCX).", "warning");
      return;
    }

    setLoading(true);
    showMessage("Submitting Information...", "info");

    try {
      const fd = new FormData();
      fd.append("firstName", form.firstName);
      fd.append("lastName", form.lastName);
      fd.append("mobile", form.mobile);
      fd.append("email", form.email);
      fd.append("resume", resumeFile);

      const res = await axios.post(`${apiBase}/api/techsummit2025/dropbox/submit`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      showMessage(res.data.message || "Details saved successfully!", "success");
      setStep("done");
    } catch (err) {
      console.error(err);
      showMessage(err?.response?.data?.message || "Failed to submit.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      elevation={6}
      sx={{
        mt: { xs: 2, sm: 4 },
        p: { xs: 2, sm: 4 },
        borderRadius: 3,
        width: { xs: "90%", sm: 600 },
        mx: "auto",
        backgroundColor: "#f7f9fc",
      }}
    >
      <Typography variant="h5" gutterBottom sx={{ color: "#0070d3", mb: 3, textAlign: "center" }}>
        Resume DropBox
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {/* Step 1: Email Check */}
      <Box
        sx={{
          mb: 3,
          p: 2,
          backgroundColor: step === "email_check" ? "#e3f2fd" : "#ffffff",
          borderRadius: 2,
          border: `1px solid ${step === "email_check" ? "#90caf9" : "#ccc"}`,
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
        >
          {step === "email_check" ? "1. Enter Email to Begin" : "1. Email Confirmed"}
          {step === "form_entry" && <CheckCircleIcon color="success" />}
        </Typography>
        <TextField
          label="Your Email Address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          disabled={loading || step === "form_entry" || step === "done"}
          sx={{ mb: 2 }}
          type="email"
          variant="outlined"
        />
        <Button
          startIcon={<PersonSearchIcon />}
          onClick={handleCheckEmail}
          disabled={loading || step === "form_entry" || step === "done"}
          variant="contained"
          color="primary"
          fullWidth
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Proceed"}
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Step 2: Form and Upload */}
      <Box
        sx={{
          p: 2,
          backgroundColor: step === "form_entry" ? "#e8f5e9" : "#ffffff",
          borderRadius: 2,
          border: `1px solid ${step === "form_entry" ? "#a5d6a7" : "#ccc"}`,
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
        >
          2. Details & Resume
          {step === "done" && <CheckCircleIcon color="success" />}
        </Typography>

        <Box sx={{ display: "grid", gap: { xs: 1, sm: 2 }, opacity: step === "form_entry" ? 1 : 0.4 }}>
          <TextField
            label="First Name (Required)"
            value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
            fullWidth
            disabled={step !== "form_entry"}
          />
          <TextField
            label="Last Name (Required)"
            value={form.lastName}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
            fullWidth
            disabled={step !== "form_entry"}
          />
          <TextField
            label="Mobile (Required)"
            value={form.mobile}
            onChange={(e) => setForm({ ...form, mobile: e.target.value })}
            fullWidth
            disabled={step !== "form_entry"}
          />
          <TextField
            label="Email (Locked)"
            value={form.email}
            fullWidth
            type="email"
            disabled
          />

          {/* Resume Upload */}
          <Box
            sx={{
              p: 2,
              border: "1px dashed #0070d3",
              borderRadius: 1,
              mt: 1,
              backgroundColor: "#e3f2fd50",
              width: "100%",
            }}
          >
            <Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>
              Upload Resume (PDF/DOCX)
            </Typography>
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileChange}
              disabled={step !== "form_entry"}
              id="resume-upload"
              style={{ display: "none" }}
            />
            <label htmlFor="resume-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                disabled={step !== "form_entry"}
              >
                {resumeFile ? "Change File" : "Select File"}
              </Button>
            </label>

            {resumeFile && (
              <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Avatar sx={{ bgcolor: "#0070d3" }}>
                  {resumeFile.name?.charAt(0)?.toUpperCase()}
                </Avatar>
                <Typography variant="body2" color="text.secondary">
                  {resumeFile.name} • {(resumeFile.size / 1024).toFixed(0)} KB
                </Typography>
              </Box>
            )}
          </Box>

          {/* Submission Buttons */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
              mt: 2,
            }}
          >
            <Button
              onClick={() => { setStep("email_check"); setLoading(false); }}
              disabled={loading || step === "done"}
              fullWidth={{ xs: true, sm: false }}
            >
              Back to Email
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="success"
              disabled={loading || step !== "form_entry"}
              fullWidth={{ xs: true, sm: false }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : "Submit"}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Done Screen */}
      {step === "done" && (
        <Box sx={{ textAlign: "center", py: 4, color: "#1b5e20" }}>
          <CheckCircleIcon sx={{ fontSize: 60, mb: 1 }} color="success" />
          <Typography variant="h5">Upload Successful!</Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            We've received your details.
          </Typography>
          <Button onClick={reset} variant="text" sx={{ mt: 2 }}>
            Upload Again.
          </Button>
        </Box>
      )}

      {/* Snackbar for Messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
